// Agents › Sessions: live and past sessions; a row opens the session with its timeline and raw events.
import { useMemo } from 'react';
import type { Interval } from '../../../packages/core/src/index.ts';
import { dayRange, weekRange } from '../lib/calendar.ts';
import { navigate } from '../lib/router.ts';
import { displayTitle } from '../model/data.ts';
import { agentCountsLabel, buildSnapshot, type SessionView } from '../model/snapshot.ts';
import { Banner, EmptyState, Figure, Loading, ScreenHeader, TextInput } from '../components/common.tsx';
import { RowButton } from '../components/pickers.tsx';
import { useFmt, useMedia, useNow, useRange, useStore } from '../store/hooks.tsx';
import type { SessionsFilter } from '../store/store.ts';
import { Button, Monogram, ProjectChip, SegmentedControl, StatusPill } from '../ui/ds.tsx';
import { Icon } from '../ui/Icon.tsx';

export function sessionsRange(filter: SessionsFilter, now: number, timeZone: string): Interval {
  const today = dayRange(now, timeZone);
  switch (filter) {
    case 'live': case 'today': return today;
    case 'week': return weekRange(now, timeZone);
    case 'month': return { start: dayRange(now, timeZone, -29).start, end: today.end };
  }
}

export function Sessions() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(1000);
  const filter = store.ui.sessionsFilter;
  const range = useMemo(() => sessionsRange(filter, now, store.timeZone), [filter, Math.floor(now / 60_000), store.timeZone]);
  const loaded = useRange(range);
  const snap = loaded.data ? buildSnapshot(store.ws, loaded.data, range, now) : undefined;
  const query = store.ui.sessionSearch.trim().toLowerCase();
  const sessions = (snap?.sessions ?? []).filter((s) => (filter !== 'live' || s.isLive) && (!query
    || [displayTitle(s.record), s.record.repoName ?? '', s.record.gitBranch ?? '', s.provider.name, s.project?.name ?? ''].some((x) => x.toLowerCase().includes(query))));
  const compact = useMedia('(max-width: 860px)');
  return (
    <div className="screen">
      <ScreenHeader title={t('Sessions')} subtitle={snap ? `${t('%lld Sessions', snap.sessions.length)} · ${agentCountsLabel(t, snap.countingAgents, snap.waitingAgents)}` : undefined}>
        <span className="search-field">
          <Icon name="search" size={16} />
          <TextInput type="search" value={store.ui.sessionSearch} placeholder={t('Sessions durchsuchen')} aria-label={t('Sessions durchsuchen')}
            onChange={(e) => store.setUi({ sessionSearch: e.target.value })} />
        </span>
        <SegmentedControl<SessionsFilter> label={t('Zeitraum')} value={filter} onChange={(v) => store.setUi({ sessionsFilter: v })} options={[
          { value: 'live', label: t('Live') }, { value: 'today', label: t('Heute') }, { value: 'week', label: t('Woche') }, { value: 'month', label: t('30 Tage') },
        ]} />
      </ScreenHeader>
      {snap && snap.unassigned.length > 0 && filter !== 'live' ? (
        <Banner icon="folder">{t('%@ ohne Projekt: %@ Agent-Zeit wird nicht abgerechnet.', t('%lld Sessions', snap.unassigned.length),
          fmt.duration(snap.unassigned.reduce((n, s) => n + s.countedMs, 0)))}</Banner>
      ) : null}
      {!snap ? (loaded.error ? <Banner tone="danger">{loaded.error}</Banner> : <Loading label={t('Lädt …')} />) : sessions.length === 0 ? (
        filter === 'live'
          ? <EmptyState icon="terminal" title={t('Gerade arbeitet kein Agent')} message={t('Sobald ein verbundener Agent arbeitet oder auf dich wartet, erscheint er hier.')} />
          : query
            ? <EmptyState icon="search" title={t('Keine Treffer')} message={t('Keine Session passt zu „%@“.', store.ui.sessionSearch)} />
            : <EmptyState icon="terminal" title={t('Noch keine Agent-Sessions')} message={t('In diesem Zeitraum hat kein verbundener Agent gearbeitet.')}>
              <Button icon="plug" onClick={() => navigate({ name: 'integrations' })}>{t('Agent verbinden')}</Button>
            </EmptyState>
      ) : (
        <>
          <div className="tl-card table sessions-table">
            {compact ? null : (
              <div className="table__head sessions-grid" aria-hidden="true">
                <span>{t('Session')}</span><span>{t('Projekt')}</span><span>{t('Status')}</span>
                <span className="table__num">{t('Gezählt')}</span><span className="table__num">{t('Gewartet')}</span>
              </div>
            )}
            {sessions.map((s) => <SessionRow key={s.record.id} session={s} now={now} compact={compact} />)}
          </div>
          <div className="figures">
            <Figure label={t('Gezählt')} value={fmt.duration(sessions.reduce((n, s) => n + s.countedMs, 0))} strong />
            <Figure label={t('Gewartet · nicht gezählt')} value={fmt.duration(sessions.reduce((n, s) => n + s.waitingMs, 0))} />
            <span className="figures__note">{t('Summe der Sessions. Abgerechnet wird pro Projekt zusammengeführt.')}</span>
          </div>
        </>
      )}
    </div>
  );
}

function SessionRow({ session: s, now, compact }: { session: SessionView; now: number; compact: boolean }) {
  const t = useStore().t;
  const fmt = useFmt();
  const r = s.record;
  const title = (
    <span className="table__cell">
      <Monogram text={s.provider.mono} provider={s.provider.id} size="sm" />
      <span className="session-name">
        <span className="session-name__title">{displayTitle(r)}</span>
        <span className="session-name__meta">
          {`${s.provider.name} · ${fmt.started(r.startedAt, now)}`}
          {r.gitBranch ? <span className="tl-mono"><Icon name="branch" size={12} />{r.gitBranch}</span> : null}
          {r.coverage === 'partial' ? <span className="note--faint">{t('Teilabdeckung')}</span> : null}
        </span>
      </span>
    </span>
  );
  const project = s.project ? <ProjectChip name={s.project.name} color={s.project.colorSlot} plain /> : <span className="note--faint">{t('ohne Projekt')}</span>;
  return (
    <RowButton className={compact ? 'table__row sessions-card' : 'table__row sessions-grid'} onClick={() => navigate({ name: 'session', id: r.id })}
      label={`${displayTitle(r)}, ${s.provider.name}, ${t('gezählt')} ${fmt.spoken(s.countedMs)}`}>
      {title}
      {compact ? (
        <span className="sessions-card__foot">
          {project}
          <StatusPill state={s.state} size="sm" />
          <span className="table__num">{fmt.duration(s.countedMs)}</span>
        </span>
      ) : (
        <>
          <span className="table__cell">{project}</span>
          <span className="table__cell"><StatusPill state={s.state} size="sm" /></span>
          <span className="table__num">{fmt.duration(s.countedMs)}</span>
          <span className="table__num table__num--muted">{fmt.duration(s.waitingMs)}</span>
        </>
      )}
    </RowButton>
  );
}
